import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

export type ConfirmDialogVariant = 'warning' | 'danger' | 'info';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'info',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const variantStyles =
    variant === 'danger'
      ? {
          primaryBg: '#DC2626',
          primaryText: '#FFFFFF',
        }
      : variant === 'warning'
        ? {
            primaryBg: '#F59E0B',
            primaryText: '#111827',
          }
        : {
            primaryBg: '#06141B',
            primaryText: '#F9FAFB',
          };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-2 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="bg-white dark:bg-[#1a1a1a] w-full max-w-md overflow-hidden flex flex-col"
        style={{
          fontFamily: 'Poppins, sans-serif',
          borderRadius: 15,
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between flex-shrink-0 p-5 pb-4"
          style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
        >
          <h2
            id="confirm-dialog-title"
            className="text-[16px] text-[#06141B] dark:text-[#F5F6F5]"
            style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80 flex-shrink-0 bg-[#06141B] dark:bg-[#F5F6F5] text-[#F5F6F5] dark:text-[#06141B]"
            aria-label="Fechar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {description && (
          <div className="p-5">
            <div
              className="text-[#06141B] dark:text-[#F5F6F5]"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, lineHeight: 1.5 }}
            >
              {description}
            </div>
          </div>
        )}

        <div
          className="px-5 py-4 flex justify-end gap-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: '#F9FAFB',
              color: '#2D3748',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: variantStyles.primaryBg,
              color: variantStyles.primaryText,
            }}
          >
            {isLoading ? 'Aguarde...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

